import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useRefundOrder } from '../hooks/use-order-mutations';
import { refundable, refundErrorKey, refundKind } from '../schemas/order-rules';
import {
  emptyRefundForm,
  refundSchema,
  toRefundBody,
  type RefundFormInput,
  type RefundFormOutput,
} from '../schemas/refund-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['amount', 'reason', 'note', 'restock', 'lines']);
const mapField = (field: string) => (field.startsWith('amount') ? 'amount' : field);

export interface RefundDialogProps {
  storeId: string;
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Refunds part or all of what was paid (D-044). A Stripe payment is refunded through Stripe on
 * the worker and shows as pending until it confirms; a manual payment is recorded only, the money
 * goes back outside Nucleus. Restocking puts shipped units back on hand and releases unshipped
 * ones.
 */
export function RefundDialog({ storeId, order, open, onOpenChange }: RefundDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const refund = useRefundOrder(storeId);
  const kind = refundKind(order);
  const balance = useMemo(() => refundable(order), [order]);
  const schema = useMemo(() => refundSchema(order), [order]);
  const form = useForm<RefundFormInput, unknown, RefundFormOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: emptyRefundForm(order),
  });
  const restock = form.watch('restock');

  const close = () => {
    onOpenChange(false);
    refund.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await refund.mutateAsync({ id: order.id, body: toRefundBody(values) });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      const key = refundErrorKey(error);
      form.setError('root.server', { type: 'server', message: key ? t(key) : message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('orders:refund.title', { name: order.name })}</DialogTitle>
          <DialogDescription>{t(`orders:refund.description.${kind}`)}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <p className="text-sm font-medium tabular-nums" data-testid="refundable">
              {t('orders:refund.refundable', { amount: format.money(balance) })}
            </p>
            <FormField
              control={form.control}
              name="amount"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('orders:refund.amount')}</FormLabel>
                  <FormControl>
                    <MoneyInput
                      currency={order.currency}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      aria-invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:refund.reason')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder={t('orders:refund.reasonPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="restock"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-1 rounded-md border p-3">
                  <div className="flex flex-row items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">{t('orders:refund.restock')}</FormLabel>
                  </div>
                  <FormDescription>{t('orders:refund.restockHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">
                {restock ? t('orders:refund.itemsRestock') : t('orders:refund.items')}
              </legend>
              {order.lines.map((line, index) => (
                <FormField
                  key={line.id}
                  control={form.control}
                  name={`lines.${index}.quantity`}
                  render={({ field, fieldState }) => (
                    <FormItem className="flex flex-row items-start justify-between gap-3 rounded-md border p-3">
                      <div className="min-w-0 space-y-0.5">
                        <FormLabel className="font-medium">{line.title}</FormLabel>
                        <FormDescription>
                          {t('orders:refund.lineState', {
                            quantity: line.quantity,
                            shipped: line.fulfilledQuantity,
                          })}
                        </FormDescription>
                        <FormMessage />
                      </div>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={line.quantity - line.cancelledQuantity}
                          className="w-20 text-right tabular-nums"
                          aria-label={t('orders:refund.quantityFor', { title: line.title })}
                          aria-invalid={fieldState.invalid}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </fieldset>
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:refund.note')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder={t(`orders:refund.notePlaceholder.${kind}`)}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={refund.isPending}>
                {refund.isPending ? t('orders:refund.submitting') : t('orders:refund.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
